import { parseDateLocal } from './utils';
import { calculateEnrollmentPremium, getAgeAsOfDate } from './enrollmentCalculator';
import { isSpouseRelationship, isChildRelationship } from '@shared/utils/enrollmentCalculator';
import { isLifeDisabilityType } from '@shared/schemas/plans';
import { calculateQLEEffectiveDate } from '@shared/utils/qleTypes';

/**
 * Coverage-start date used both to pick the correct PLAN YEAR and as the
 * enrollment effective date, computed by a standard rule (NOT the per-plan
 * entryDate — that config is unreliable across renewal years):
 *
 *  - QLE       → `first_of_month`: event on the 1st = that day, else 1st of next month.
 *                (event 7/1 → 7/1 → current plan; event 7/15 → 8/1 → renewal plan)
 *  - new hire  → `first_of_following_month`: always the 1st of the next month.
 *                (hired 6/18 → 7/1 → current plan; hired 7/1 → 8/1 → renewal plan)
 *
 * The two rules differ only when the date lands on the 1st — which is exactly the
 * case the client flagged for QLEs.
 */
export function coverageStartForEvent(
  eventDate: Date | null | undefined,
  mode: "first_of_month" | "first_of_following_month",
): Date | null {
  if (!eventDate) return null;
  const d = eventDate instanceof Date ? eventDate : parseDateLocal(eventDate as any);
  if (!d || isNaN(d.getTime())) return null;
  return calculateQLEEffectiveDate(d, mode);
}

/**
 * Keep only plans whose coverage-year window [effectiveDate, endDate] contains the
 * coverage-start date. Fixes the bug where a QLE/new-hire enrollee saw BOTH the
 * current plan year and the upcoming renewal year (e.g. an Aug 1 coverage start
 * should only offer the Aug-1–Jul-31 plan, not last year's expiring plan).
 *
 * Plans missing a window bound are treated as open on that side (not filtered out),
 * so this never removes plans we can't position in time.
 */
export function filterPlansByCoverageWindow(plans: any[], coverageStart: Date | null): any[] {
  if (!coverageStart || !Array.isArray(plans)) return plans;
  const c = coverageStart.getTime();
  return plans.filter((p) => {
    const start = p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null;
    const end = p?.endDate ? parseDateLocal(p.endDate) : null;
    if (start && c < start.getTime()) return false;
    if (end && c > end.getTime()) return false;
    return true;
  });
}

/**
 * Clamp a computed effective date so coverage never starts before the plan year
 * itself begins. e.g. a `first_of_month` plan whose rule yields Jul 1 but whose
 * coverage year starts Aug 1 should enroll the employee effective Aug 1.
 *
 * Date-in/Date-out variant for the QLE flow. For blank-default + validation
 * during broker config, use `resolveEnrollmentEffectiveDate` in shared/lib/ageDetermination.
 */
export function clampEffectiveToPlanStart(effectiveDate: Date, plan: any): Date {
  const start = plan?.effectiveDate ? parseDateLocal(plan.effectiveDate) : null;
  if (start && effectiveDate.getTime() < start.getTime()) return start;
  return effectiveDate;
}

/**
 * Resolve which plans to show based on enrollment state.
 * - QLE / new_hire: all active client plans (not constrained by OE selections)
 * - OE with a configured window: the broker's selection is authoritative —
 *   an empty selection offers NOTHING until plans are picked in
 *   Client → Benefits → Open Enrollment.
 * - No OE window at all: all active client plans (browsing fallback).
 */
export function resolveAvailablePlans(
  enrollmentState: string,
  eligibleOePlans: any[],
  brokerFilteredPlans: any[],
  activeClientPlans: any[],
  hasOeWindow: boolean,
): any[] {
  if (enrollmentState === "qualifying_event") return activeClientPlans;
  if (enrollmentState === "new_hire") return activeClientPlans;
  if (hasOeWindow) {
    // brokerFilteredPlans (selected plans, no date filtering) still honors the
    // broker's curation when date windows exclude everything; it is [] when
    // the selection is empty, so an unconfigured window offers nothing.
    return eligibleOePlans.length > 0 ? eligibleOePlans : brokerFilteredPlans;
  }
  return activeClientPlans;
}

export interface EnrollmentLike {
  status?: string | null;
  waivedCoverage?: boolean | null;
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
}

/**
 * Check if an enrollment is active (status === "active", not waived, not ended)
 */
export function isActiveEnrollment(enrollment: EnrollmentLike): boolean {
  const status = String(enrollment?.status || "").toLowerCase();
  const isWaived = Boolean(enrollment?.waivedCoverage);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = enrollment?.endDate ? parseDateLocal(enrollment.endDate) : null;
  const ended = endDate ? endDate < today : false;

  // Exclude future-effective enrollments — they belong in pending, not active
  const effDate = enrollment?.effectiveDate ? parseDateLocal(enrollment.effectiveDate) : null;
  const notYetEffective = effDate ? effDate > today : false;

  // Include terminated enrollments that haven't reached their end date yet
  // (still in effect, should show with "Pending termination" badge)
  const isActive = status === "active" && !ended && !notYetEffective && !isWaived;
  const isTerminatedButStillEffective = status === "terminated" && !ended && !!endDate && !notYetEffective;
  // A future-dated waiver still provides coverage until its endDate, so it counts
  // as active during that window — it only becomes "waived" after the endDate.
  // (Plain waived-now rows have no future endDate, so they stay excluded.)
  const isWaivedButStillEffective = isWaived && !ended && !!endDate && !notYetEffective;
  return isActive || isTerminatedButStillEffective || isWaivedButStillEffective;
}

/**
 * Check if an enrollment is currently effective (active AND effectiveDate <= today).
 * Use this for cost/deduction calculations where only currently-effective enrollments should count.
 * Unlike isActiveEnrollment, this excludes future-effective enrollments.
 */
export function isCurrentlyEffectiveEnrollment(enrollment: EnrollmentLike): boolean {
  if (!isActiveEnrollment(enrollment)) return false;
  if (!enrollment?.effectiveDate) return true; // no date = assume current
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const effDate = parseDateLocal(enrollment.effectiveDate);
  return effDate ? effDate <= today : true;
}

/**
 * Check if an enrollment is waived
 */
export function isWaivedEnrollment(enrollment: EnrollmentLike): boolean {
  const isWaived = Boolean(enrollment?.waivedCoverage);
  const status = String(enrollment?.status || "").toLowerCase();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = enrollment?.endDate ? parseDateLocal(enrollment.endDate) : null;
  const ended = endDate ? endDate <= today : false;
  const effDate = enrollment?.effectiveDate ? parseDateLocal(enrollment.effectiveDate) : null;
  const notYetEffective = effDate ? effDate > today : false;

  // A future-dated waiver is still providing coverage until its endDate, so it's
  // not "waived" yet (shows as active/terminating until then).
  const stillEffective = !!endDate && endDate >= today && !notYetEffective;
  if ((isWaived || status === "waived") && stillEffective) return false;

  return isWaived || status === "waived" || (status === "terminated" && isWaived && !ended);
}

/**
 * Check if an enrollment is pending (future effective date)
 * Returns true only if effective date is strictly after today
 * If effective date is today or in the past, returns false (active)
 */
export function isPendingEnrollment(enrollment: EnrollmentLike): boolean {
  if (!enrollment?.effectiveDate) return false;
  const status = String(enrollment?.status || "").toLowerCase();
  if (status === "terminated") return false;

  // Get today's date as YYYY-MM-DD string in local timezone
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Extract YYYY-MM-DD from effective date
  const effDate = enrollment.effectiveDate;
  const effDateStr = effDate instanceof Date
    ? `${effDate.getFullYear()}-${String(effDate.getMonth() + 1).padStart(2, '0')}-${String(effDate.getDate()).padStart(2, '0')}`
    : String(effDate).split('T')[0];

  // Compare as strings - only pending if effectiveDate is strictly after today
  return effDateStr > todayStr;
}

/**
 * Check if an enrollment is ending soon (has future end date)
 */
export function isEndingEnrollment(enrollment: EnrollmentLike): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = enrollment?.endDate ? parseDateLocal(enrollment.endDate) : null;

  return endDate ? endDate >= today : false;
}

/**
 * A terminated (plan switch / termination) or waived row whose endDate is today or
 * later is still in force — badge it "Active" + "Terminating <date>", not
 * Terminated/Waived. Mirrors isActiveEnrollment's still-effective cases.
 */
export function isStillEffectiveEnding(enrollment: EnrollmentLike): boolean {
  const status = String(enrollment?.status || "").toLowerCase();
  const isWaived = Boolean(enrollment?.waivedCoverage) || status === "waived";
  return (
    isEndingEnrollment(enrollment) &&
    !isPendingEnrollment(enrollment) &&
    (status === "terminated" || isWaived)
  );
}

/**
 * Filter array to only active enrollments
 */
export function filterActiveEnrollments<T extends EnrollmentLike>(
  enrollments: T[]
): T[] {
  return enrollments.filter(isActiveEnrollment);
}

/**
 * Number of distinct plans currently in force (isActiveEnrollment) for an
 * employee. Employee-level rows only — dependent rows (dependentId set) don't
 * add to the count, and repeat rows for the same plan count once. Ended
 * (terminated / past endDate), waived and future-effective rows are excluded.
 */
export function countInForcePlans(
  enrollments: Array<EnrollmentLike & { planId?: number | null; dependentId?: number | null }>
): number {
  const planIds = new Set<number>();
  for (const e of enrollments || []) {
    if (e?.dependentId != null || e?.planId == null) continue;
    if (isActiveEnrollment(e)) planIds.add(e.planId);
  }
  return planIds.size;
}

/**
 * Filter array to only waived enrollments
 */
export function filterWaivedEnrollments<T extends EnrollmentLike>(
  enrollments: T[]
): T[] {
  return enrollments.filter(isWaivedEnrollment);
}

/**
 * True when a row represents a *dependent* who is *actively* covered on
 * the given plan. Used everywhere we list "covered dependents" so waived
 * dep enrollments (e.g. spouse opted out of a plan the rest of the family
 * enrolled in) don't sneak into "currently covered" lists.
 */
export function isActiveDependentEnrollmentForPlan(
  row: EnrollmentLike & { planId?: number | null; dependentId?: number | null },
  planId: number,
): boolean {
  return (
    row?.planId === planId &&
    Number.isFinite(row?.dependentId) &&
    isActiveEnrollment(row)
  );
}

/**
 * True when a dep enrollment row is either currently active OR pending/future
 * effective. Use this when displaying "who will be covered" or computing
 * "which deps the user has elected" — both surfaces should reflect imminent
 * coverage, not just rows whose effective date has already passed.
 *
 * Excludes terminated, deleted, and waived rows. Use
 * `isActiveDependentEnrollmentForPlan` when you specifically need strict-active.
 */
export function isCurrentOrUpcomingDependentEnrollmentForPlan(
  row: EnrollmentLike & { planId?: number | null; dependentId?: number | null },
  planId: number,
): boolean {
  if (row?.planId !== planId) return false;
  if (!Number.isFinite(row?.dependentId)) return false;
  if (row?.waivedCoverage === true) return false;
  const status = String((row as any)?.status ?? "").toLowerCase();
  return status === "active" || status === "pending";
}

/**
 * Determine if a plan is currently available (effectiveDate <= today AND endDate not passed).
 * Excludes future-effective and closed/expired plans.
 */
export function isCurrentlyAvailablePlan(plan: { effectiveDate?: string | Date | null; endDate?: string | Date | null }): boolean {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const effectiveDate = plan.effectiveDate ? parseDateLocal(plan.effectiveDate) : null;
  const endDate = plan.endDate ? parseDateLocal(plan.endDate) : null;
  if (effectiveDate) effectiveDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);
  if (effectiveDate && effectiveDate > now) return false;
  if (endDate && endDate < now) return false;
  return true;
}

/**
 * Determine coverage tier from selected dependents
 * Matches the inline pattern used in Benefits.tsx for consistency
 */
export function determineCoverageTierFromDependents(
  allDependents: any[],
  selectedDependentIds: number[]
): string {
  const selectedDeps = allDependents.filter((d: any) =>
    selectedDependentIds.includes(d.id)
  );

  // Use the canonical classifiers so every new child variant (child_step,
  // child_adopted, child_foster, child_ward, grandchild) and domestic_partner
  // route correctly into employee_spouse / employee_children / family tiers.
  const spouseSelected = selectedDeps.some((d: any) => isSpouseRelationship(d.relationship));
  const childSelected = selectedDeps.filter((d: any) => isChildRelationship(d.relationship));

  if (spouseSelected && childSelected.length > 0) return "family";
  if (spouseSelected) return "employee_spouse";
  if (childSelected.length > 0) return "employee_children";
  return "employee_only";
}

/**
 * Single source of truth for member-level premium computation. Returns the full
 * EnrollmentCalculationResult so callers can read members/total/employeePremium.
 *
 * Centralizes the two inputs that previously diverged between the benefits card
 * and the modify modal:
 *   - age-as-of date: derived from `effectiveDate` via getAgeAsOfDate. When no
 *     effectiveDate is given, ageAsOf is left undefined (calc defaults to today)
 *     — this avoids getAgeAsOfDate's employee.hireDate fallback, which rated
 *     members years too young.
 *   - coverage tier: always derived from the covered members (self-heals a stale
 *     stored tier, e.g. "family" with only a spouse enrolled).
 */
export function computeEnrollmentPremiumResult(params: {
  plan: any;
  planRates: any[];
  planContributions: any[];
  /** Contribution + rate rows across all plans (incl. referenced base plans).
   * BOTH are required to resolve a buy-up plan's employer contribution — the
   * base plan's percentages (allPlanContributions) applied to the base plan's
   * rates (allPlanRates). Omitting allPlanRates leaves the employer share
   * unresolved and bills the employee the full premium. */
  allPlanContributions?: any[];
  allPlanRates?: any[];
  employee: any;
  /** Members to cover — the actually-covered set (card) or the edited selection (modal). */
  dependents: any[];
  /** Enrollment effective date; drives the age-rating date. */
  effectiveDate?: string | Date | null;
  ldConfig?: any | null;
}): any {
  const { plan, planRates, planContributions, allPlanContributions, allPlanRates, employee, dependents, effectiveDate, ldConfig } = params;
  if (!plan || !employee) return null;
  try {
    const isLD = isLifeDisabilityType(String((plan as any)?.type || ''));
    const ageAsOf = effectiveDate ? getAgeAsOfDate(plan, employee, effectiveDate) : undefined;
    const depList = dependents || [];
    const coverageTier = determineCoverageTierFromDependents(depList, depList.map((d: any) => d.id));
    return calculateEnrollmentPremium({
      plan,
      planRates: planRates || [],
      planContributions: (planContributions || []) as any,
      employee,
      dependents: depList,
      coverageTier,
      ageAsOf,
      allPlanContributions: allPlanContributions as any,
      allPlanRates: allPlanRates as any,
      lifeDisabilityConfig: isLD ? (ldConfig ?? null) : undefined,
      employeeCompensation: isLD
        ? {
            compensationType: (employee as any)?.compensationType,
            compensationAmount: (employee as any)?.compensationAmount,
            salary: (employee as any)?.salary,
          }
        : undefined,
    });
  } catch (err) {
    console.warn('[computeEnrollmentPremiumResult]', err);
    return null;
  }
}

type SelectionPremiumParams = {
  plan: any;
  ratesByPlanId: Record<number, any[]>;
  contribsByPlanId: Record<number, any[]>;
  employee: any;
  selectedDependentIds: number[];
  allDependents: any[];
  effectiveDate?: string | Date | null;
  ldConfig?: any | null;
  /** Map form of ldConfig (useLifeDisabilityConfigs); an explicit ldConfig wins. */
  ldConfigByPlanId?: Record<number, any> | null;
};

/**
 * Full premium result (total / employer / employee + per-member rows) for a
 * plan selection. Map-based inputs; delegates to computeEnrollmentPremiumResult.
 */
export function computePremiumResultForSelection(params: SelectionPremiumParams): any {
  const { plan, ratesByPlanId, contribsByPlanId, employee, selectedDependentIds, allDependents, effectiveDate, ldConfigByPlanId } = params;
  const ldConfig = params.ldConfig ?? ldConfigByPlanId?.[plan?.id] ?? null;
  const selectedMembers = (allDependents || []).filter((d: any) => selectedDependentIds.includes(d.id));
  // Flatten every fetched plan's rows as allPlanContributions/allPlanRates so a
  // buy-up plan's employer contribution resolves from its referenced base plan.
  // Requires the base plan's rows to be present in the maps (callers that only
  // fetch the employee's available plans must also fetch referenced base plans).
  return computeEnrollmentPremiumResult({
    plan,
    planRates: ratesByPlanId?.[plan.id] || [],
    planContributions: contribsByPlanId?.[plan.id] || [],
    allPlanContributions: Object.values(contribsByPlanId || {}).flat(),
    allPlanRates: Object.values(ratesByPlanId || {}).flat(),
    employee: employee || {},
    dependents: selectedMembers,
    effectiveDate,
    ldConfig,
  });
}

/** Convenience wrapper returning just the employee premium for a plan selection. */
export function calculatePremiumForSelection(params: SelectionPremiumParams): number {
  return computePremiumResultForSelection(params)?.employeePremium ?? 0;
}
